import { ChevronDown, XClose } from "@icons/untitled_ui_icons";
import { ReactComponent as CheckmarkSVG } from "@svg/checkmark.svg";
import { DetailedHTMLProps, HTMLAttributes } from "react";
import {
  ClearIndicatorProps,
  components,
  ControlProps,
  DropdownIndicatorProps,
  GroupBase,
  MenuProps,
  MultiValueRemoveProps,
  OptionProps,
  ValueContainerProps,
} from "react-select";
import { Stack } from "../../Stack/Stack";
import { SelectInputOption } from "./SelectInput.types";

type InnerPropsType = DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement> & { "data-cy": string };

export const SelectInput_CustomValueContainer = <IsMulti extends boolean>(
  props: ValueContainerProps<SelectInputOption, IsMulti, GroupBase<SelectInputOption>>
) => (
  <components.ValueContainer
    {...props}
    innerProps={
      {
        ...props?.innerProps,
        "data-cy": "value",
      } as InnerPropsType
    }
  />
);

export const SelectInput_CustomControl = <IsMulti extends boolean>({
  children,
  ...props
}: ControlProps<SelectInputOption, IsMulti, GroupBase<SelectInputOption>>) => (
  <components.Control
    {...props}
    innerProps={
      {
        ...props?.innerProps,
        "data-cy": props.selectProps.dataCy && `${props.selectProps.dataCy}-control`,
      } as InnerPropsType
    }
  >
    {props.selectProps.elementBefore && props.selectProps.elementBefore}
    {props.selectProps.iconBefore && (
      <props.selectProps.iconBefore width={20} height={20} color="var(--gray-500)" style={{ position: "absolute", left: "12px", zIndex: 1 }} />
    )}
    {children}
  </components.Control>
);

export const SelectInput_CustomMultiValueRemove = <IsMulti extends boolean>(
  props: MultiValueRemoveProps<SelectInputOption, IsMulti, GroupBase<SelectInputOption>>
) => (
  <components.MultiValueRemove
    {...props}
    innerProps={{ ...props.innerProps, "data-cy": "selectedItem-remove-icon" } as HTMLAttributes<HTMLDivElement>}
  />
);

export const SelectInput_CustomOption = <IsMulti extends boolean>(props: OptionProps<SelectInputOption, IsMulti, GroupBase<SelectInputOption>>) => (
  <components.Option
    {...props}
    innerProps={
      {
        ...props.innerProps,
        "data-cy": props.selectProps.dataCy && `${props.selectProps.dataCy}-option-${props.label}`,
      } as InnerPropsType
    }
  >
    <Stack justifyContent="space-between" width="100%" alignItems="center">
      <Stack direction="row" alignItems="center" spacing="var(--spacing-2)">
        {props.data.elementBefore && props.data.elementBefore}
        {props.data.iconBefore && <props.data.iconBefore width={20} height={20} color="var(--gray-500)" style={{ flexShrink: 0 }} />}
        {props.data.label}
      </Stack>
      {props.isSelected && <CheckmarkSVG style={{ color: "var(--brand-600)", width: 20, height: 20 }} />}
    </Stack>
  </components.Option>
);

export const SelectInput_CustomClearIndicator = <IsMulti extends boolean>(
  props: ClearIndicatorProps<SelectInputOption, IsMulti, GroupBase<SelectInputOption>>
) => (
  <components.ClearIndicator {...props} innerProps={{ ...props.innerProps, "data-cy": "clear-all-button" } as InnerPropsType}>
    <XClose width={20} height={20} color="var(--gray-400)" />
  </components.ClearIndicator>
);

export const SelectInput_CustomDropdownIndicator = <IsMulti extends boolean>(
  props: DropdownIndicatorProps<SelectInputOption, IsMulti, GroupBase<SelectInputOption>>
) => (
  <components.DropdownIndicator {...props} innerProps={{ ...props.innerProps, "data-cy": "select-dropdown-indicator" } as InnerPropsType}>
    <ChevronDown color="var(--gray-500)" height={20} width={20} />
  </components.DropdownIndicator>
);

export const SelectInput_CustomMenu = <IsMulti extends boolean>(props: MenuProps<SelectInputOption, IsMulti, GroupBase<SelectInputOption>>) => (
  <components.Menu {...props}>
    {props.selectProps.customHeader}
    {props.children}
  </components.Menu>
);
